import axios from "axios";
import config from "../../config";

/**
 * The Olli assistant (backend: /api/olli, items 1, 130, 145).
 *
 *   POST   /api/olli/chats/messages { chat_id?, message, page_path?, facility_id? }
 *            → { chat_id, reply: { text, links, steps, actions, facts, chips, sources } }
 *            A turn Olli can't answer is an error (503/504 OLLI_AI_UNAVAILABLE,
 *            OLLI_AI_TIMEOUT, OLLI_LOOKUP_FAILED; 429 OLLI_RATE_LIMIT) and is not
 *            saved, not even as a new chat, so a retry starts clean.
 *   GET    /api/olli/chats            → [{ id, title, updated_at }]
 *   GET    /api/olli/chats/:id        → { id, title, messages: [{ role, text, links, steps, actions, facts, created_at }] }
 *            (chips and sources are only on a live reply, not stored)
 *   DELETE /api/olli/chats/:id
 *
 * Olli answers read-only lookups on the clinic's own data (several per
 * question); facts and links are built from that data, never by the model. A
 * link names a kind of screen and, for one record, its id; it maps to a route
 * here. A lead it understood comes back as a prefill for the lead form, and
 * nothing is created until the person saves.
 */
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// The backend's link types (services/olliAssistant.js) → the panel's routes.
// A function takes the link's id; a type with no screen of its own for one
// record opens the list instead (knowledge_document: no document route).
const LINK_PATH = {
  leads_list: "/crm/leads",
  lead_detail: (id) => `/crm/leads/${id}`,
  lead_new: "/crm/leads/new",
  appointments: "/appointments",
  appointment_detail: (id) => `/appointments/${id}`,
  patients: "/patients",
  patient_detail: (id) => `/patients/${id}`,
  whatsapp: "/waba/conversations",
  whatsapp_campaigns: "/waba/campaigns",
  whatsapp_campaign_detail: (id) => `/waba/campaigns/${id}`,
  complaints: "/support/complaints",
  complaint_detail: (id) => `/support/complaints/${id}`,
  subscription: "/subscription/manage",
  wallet: "/wallet",
  ad_campaigns: "/ads/campaigns",
  // link_id is Meta's campaign id (ads_summary top_campaigns, external_campaign_id),
  // which the campaign page looks up (GET /api/ads/campaigns/:id).
  ad_campaign_detail: (id) => `/ads/campaigns/${encodeURIComponent(id)}`,
  ai_agents: "/settings/ai-agent",
  ai_agent_detail: "/settings/ai-agent",
  knowledge_base: "/knowledge-base",
  knowledge_document: "/knowledge-base",
};

function linkTarget(l, prefill) {
  const route = LINK_PATH[l.link_type];
  if (!route) return null;
  const id = l.link_id ?? null;
  // A campaign link without an id still opens the campaigns list.
  if (typeof route === "function" && id === null) return l.link_type === "ad_campaign_detail" ? { href: LINK_PATH.ad_campaigns } : null;
  const href = typeof route === "function" ? route(id) : route;
  // The lead form reads these and shows "Filled by Olli" (item 145).
  if (l.link_type === "lead_new" && prefill) return { href, state: { olliPrefill: prefill.fields || {} } };
  // The agents screen opens one agent from router state.
  if (l.link_type === "ai_agent_detail" && id !== null) return { href, state: { openAgentId: id } };
  return { href };
}

/**
 * A backend message → the thread view's message
 * ({ id, role, text, steps, facts, links, chips, sources }).
 */
export function toThreadMessage(m, id) {
  const prefill = (m.actions || []).find((a) => a.type === "prefill_form" && a.form === "lead");
  const rawLinks = m.links || [];
  // Documents the answer came from: a live reply lists them; a stored one still has their links.
  const sources = Array.isArray(m.sources) && m.sources.length
    ? m.sources.map((s) => ({ label: s.document, href: LINK_PATH.knowledge_document }))
    : rawLinks.filter((l) => l.link_type === "knowledge_document").map((l) => ({ label: l.label, href: LINK_PATH.knowledge_document }));
  let text = m.text || "";
  // The backend appends "Sources: a, b" when the answer didn't name them; the list below shows them.
  if (sources.length) text = text.replace(/\n?Sources: [^\n]*$/, "").trimEnd();
  const seen = new Set();
  const links = rawLinks
    .filter((l) => l.link_type !== "knowledge_document")
    .map((l) => {
      const target = linkTarget(l, prefill);
      if (!target) return null;
      const key = `${target.href}|${l.label}`;
      if (seen.has(key)) return null;
      seen.add(key);
      return { label: l.label, ...target };
    })
    .filter(Boolean);
  return {
    id: id ?? `${m.role}-${m.created_at || Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    role: m.role === "user" ? "user" : "olli",
    text,
    // "failed" = a lookup that errored; the thread shows it as such, not as still running.
    steps: (m.steps || []).map((s) => ({ label: s.text, status: s.status === "done" || s.status === "failed" ? s.status : "pending" })),
    // Document names are in `sources`; the label/value rows are the rest.
    facts: (m.facts || []).filter((f) => f && f.label && !(sources.length && f.label === "Source")),
    links,
    chips: (m.chips || []).filter((c) => typeof c === "string" && c.trim()),
    sources,
  };
}

/**
 * Why a turn failed, for the error bubble: { message, code, canRetry }.
 * The backend's message is shown as is; a rate limit or a refused request has no Retry.
 */
export function olliErrorOf(err) {
  const status = err?.response?.status ?? 0;
  const body = err?.response?.data || {};
  const code = body.code || null;
  if (!status) return { message: "Can't reach Olli right now. Check your connection and try again.", code: "NETWORK", canRetry: true };
  if (code === "PERMISSION_DENIED" || status === 403) return { message: body.message || "You don't have access to Ask Olli. Ask an admin to give you access.", code: code || "FORBIDDEN", canRetry: false };
  if (code === "OLLI_RATE_LIMIT" || status === 429) return { message: body.message || "You've asked Olli a lot in the last hour. Please try again a little later.", code: "OLLI_RATE_LIMIT", canRetry: false };
  if (status === 400) return { message: body.message || "Olli couldn't read that message.", code: "BAD_REQUEST", canRetry: false };
  if (status === 401) return { message: "Your session ended. Sign in again.", code: "UNAUTHORIZED", canRetry: false };
  return { message: body.message || "Olli couldn't answer just now. Please try again.", code: code || "ERROR", canRetry: true };
}

export async function sendToOlli({ message, chatId = null, pagePath = window.location.pathname, facilityId = null }) {
  const r = await axios.post(`${config.API_BASE_URL}/api/olli/chats/messages`, {
    ...(chatId ? { chat_id: chatId } : {}), message, page_path: pagePath, ...(facilityId ? { facility_id: facilityId } : {}),
  }, auth());
  const d = r.data?.data || {};
  return { chatId: d.chat_id, reply: toThreadMessage({ role: "olli", ...(d.reply || {}) }) };
}

/** Your chats, newest first. Throws when they can't be loaded (the list shows why). */
export async function listOlliChats() {
  const r = await axios.get(`${config.API_BASE_URL}/api/olli/chats`, auth());
  return (r.data?.data || []).map((c) => ({ id: c.id, title: c.title || "Chat", surfaceLabel: "Ask Olli", updatedAt: new Date(c.updated_at).getTime() }));
}

export async function getOlliChat(id) {
  const r = await axios.get(`${config.API_BASE_URL}/api/olli/chats/${id}`, auth());
  return (r.data?.data?.messages || []).map((m, i) => toThreadMessage(m, `${id}-${i}`));
}

export const deleteOlliChat = (id) => axios.delete(`${config.API_BASE_URL}/api/olli/chats/${id}`, auth());

/** The Ask Olli page with one chat open. */
export const askOlliPath = (chatId) => (chatId ? `/ask-olli?chat=${chatId}` : "/ask-olli");

/**
 * Open the floating assistant with a prompt (the Home box, item 130).
 * `surface` names the prompt box it came from, so its recent prompt can link to the chat.
 */
export const openOlli = (prompt, { surface } = {}) => window.dispatchEvent(new CustomEvent("olli:ask", { detail: { prompt, surface } }));
